import BreadcrumbComponent from '@/components/breadcrumb';
import RequestDialog from '@/components/dialogs/leades/request-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLeads } from '@/hooks/useLeads';
import { useEffect, useMemo, useState } from 'react';

const COLORS = [
  '#f87171', // red
  '#fb923c', // orange
  '#facc15', // yellow
  '#4ade80', // green
  '#2dd4bf', // teal
  '#38bdf8', // sky
  '#818cf8', // indigo
  '#a78bfa', // purple
  '#f472b6', // pink
  '#94a3b8', // gray
];

export default function LeadsPage() {
  const { leads, addLead, addColumn, getRequestsByLeadAndColumn } = useLeads();

  // https://chatgpt.com/c/68b2dd98-1924-8325-90ad-93b67b574cab

  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newLeadName, setNewLeadName] = useState('');
  const [openDialog, setOpenDialog] = useState(false);

  const [openColumnDialog, setOpenColumnDialog] = useState(false);
  const [openAddRequestDialog, setOpenAddRequestDialog] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnColor, setNewColumnColor] = useState(COLORS[0]);

  // requests state
  const [requests, setRequests] = useState({}); // { columnId: [req1, req2] }

  // agar selectedLeadId mavjud bo'lmasa, default leadni tanlaymiz
  useEffect(() => {
    if (leads.length > 0) {
      const exists = leads.some((lead) => lead.leadId === selectedLeadId);
      if (!exists) {
        setSelectedLeadId(leads[0].leadId);
      }
    }
  }, [leads, selectedLeadId]);

  const selectedLead = useMemo(() => {
    return leads.find((lead) => lead.leadId === selectedLeadId) || leads[0];
  }, [leads, selectedLeadId]);

  // requestsni yuklash
  useEffect(() => {
    const loadRequests = async () => {
      if (!selectedLead) return;
      const reqsByCol = {};
      for (const col of selectedLead.columns || []) {
        const reqs = await getRequestsByLeadAndColumn(
          selectedLead.leadId,
          col.id
        );
        reqsByCol[col.id] = reqs;
      }
      setRequests(reqsByCol);
    };
    loadRequests();
  }, [selectedLead, getRequestsByLeadAndColumn]);

  const filteredColumns = useMemo(() => {
    if (!selectedLead) return [];
    return selectedLead.columns.map((col) => {
      const colRequests = requests[col.id] || [];
      return {
        ...col,
        requests: colRequests.filter((req) =>
          req.fullName?.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      };
    });
  }, [selectedLead, requests, searchQuery]);

  const handleAddLead = async () => {
    if (!newLeadName) return;
    await addLead(newLeadName);
    setNewLeadName('');
    setOpenDialog(false);
  };

  const handleAddColumn = async () => {
    if (!newColumnName || !selectedLead) return;
    await addColumn(selectedLead.leadId, newColumnName, newColumnColor);
    setNewColumnName('');
    setNewColumnColor(COLORS[0]);
    setOpenColumnDialog(false);
  };

  return (
    <div className="px-4 lg:px-8 mx-auto my-4 space-y-4">
      <BreadcrumbComponent title="Leadlar" />

      <RequestDialog
        open={openAddRequestDialog}
        onClose={setOpenAddRequestDialog}
        leadId={selectedLeadId}
        columns={selectedLead?.columns || []}
      />

      <div>
        <h2 className="text-2xl font-bold tracking-tight">Leadlar ro'yxati</h2>
        <p className="text-muted-foreground">
          Bu yerda barcha leadlar, columnlar va ularning requestlarini
          boshqarishingiz mumkin.
        </p>
      </div>

      <Input
        placeholder="Search requests..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-96"
      />

      <div className="flex items-center justify-between">
        <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Select Lead" />
          </SelectTrigger>
          <SelectContent>
            {leads.map((lead) => (
              <SelectItem key={lead.leadId} value={lead.leadId}>
                {lead.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex gap-2">
          <Button variant="outline">Export</Button>
          <Button
            variant="secondary"
            onClick={() => setOpenAddRequestDialog(true)}
          >
            Yangi request qilish
          </Button>

          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogTrigger asChild>
              <Button>+ Add New Lead</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Yangi Lead qo'shish</DialogTitle>
              </DialogHeader>
              <Input
                placeholder="Lead nomi"
                value={newLeadName}
                onChange={(e) => setNewLeadName(e.target.value)}
              />
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setOpenDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddLead}>Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Columns */}
      <div className="flex items-start gap-4">
        {filteredColumns.map((col) => (
          <Card
            key={col.id}
            className="w-96"
            style={{ borderTop: `6px solid ${col.color || '#ccc'}` }}
          >
            <CardHeader>
              <CardTitle>{col.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {col.requests?.length === 0 && (
                <p className="text-gray-400">Hech qanday request yo'q</p>
              )}
              {col.requests?.map((req) => (
                <div
                  key={req.requestId}
                  className="p-2 bg-white rounded shadow"
                >
                  <p className="font-semibold">{req.fullName}</p>
                  <p className="text-sm text-gray-500">{req.phone}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        {/* Add new column */}
        <Dialog open={openColumnDialog} onOpenChange={setOpenColumnDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-96">
              + Add New Column
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yangi Column qo'shish</DialogTitle>
            </DialogHeader>
            <Input
              placeholder="Column nomi"
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {COLORS.map((color) => (
                <div
                  key={color}
                  onClick={() => setNewColumnColor(color)}
                  className={`w-8 h-8 rounded cursor-pointer border relative flex items-center justify-center ${
                    newColumnColor === color ? 'ring-2 ring-black' : ''
                  }`}
                  style={{ backgroundColor: color }}
                >
                  {newColumnColor === color && (
                    <span className="text-white text-sm font-bold">✓</span>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setOpenColumnDialog(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleAddColumn}>Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
